import React, { useState, useEffect, useMemo, useCallback } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import api from '../api/axios';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';

const sumar30Min = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const d = new Date();
  d.setHours(h, m + 30, 0, 0);
  return d.toTimeString().slice(0, 5);
};

const asegurarHoraEnLista = (lista, h) =>
  h && !lista.includes(h) ? [...lista, h].sort() : lista;

function AutocompleteProveedor({
  value,
  onChange,
  onPick,
  options = [],
  loading,
  placeholder = 'Escribí o seleccioná'
}) {
  const [abierto, setAbierto] = useState(false);
  const [highlight, setHighlight] = useState(-1);

  const sugerencias = useMemo(() => {
    const q = (value || '').toLowerCase().trim();
    if (!q) return options.slice(0, 8);
    return options
      .filter(o => (o?.nombre || '').toLowerCase().includes(q))
      .slice(0, 8);
  }, [value, options]);

  const pick = (sug) => {
    onChange(sug?.nombre || '');
    onPick?.(sug || null);
    setAbierto(false);
    setHighlight(-1);
  };

  const onKeyDown = (e) => {
    if (!abierto && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setAbierto(true);
      return;
    }
    if (!sugerencias.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => (h + 1) % sugerencias.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => (h - 1 + sugerencias.length) % sugerencias.length);
    } else if (e.key === 'Enter') {
      if (highlight >= 0) {
        e.preventDefault();
        pick(sugerencias[highlight]);
      }
    } else if (e.key === 'Escape') {
      setAbierto(false);
      setHighlight(-1);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        type="text"
        className="form-control"
        value={value}
        onChange={(e) => { onChange(e.target.value); setAbierto(true); }}
        onFocus={() => setAbierto(true)}
        onBlur={() => setTimeout(() => setAbierto(false), 120)}
        onKeyDown={onKeyDown}
        placeholder={loading ? 'Cargando proveedores...' : placeholder}
        autoComplete="off"
        required
      />
      {abierto && sugerencias.length > 0 && (
        <ul
          className="list-group"
          style={{
            position: 'absolute',
            zIndex: 10,
            width: '100%',
            top: '100%',
            left: 0,
            maxHeight: 220,
            overflowY: 'auto',
            boxShadow: '0 6px 18px rgba(0,0,0,.08)'
          }}
        >
          {sugerencias.map((sug, i) => (
            <li
              key={sug.id ?? `${sug.nombre}-${i}`}
              className={`list-group-item ${i === highlight ? 'active' : ''}`}
              style={{ cursor: 'pointer' }}
              onMouseEnter={() => setHighlight(i)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(sug)}
              title={sug.codigo ? `Código: ${sug.codigo}` : undefined}
            >
              {sug.nombre}{sug.codigo ? ` — ${sug.codigo}` : ''}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const SolicitarTurno = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fechaTurno, setFechaTurno] = useState(null);
  const [horasDisponibles, setHorasDisponibles] = useState([]);
  const [horaDesde, setHoraDesde] = useState('');
  const [horaHasta, setHoraHasta] = useState('');
  const [fechasDisponibles, setFechasDisponibles] = useState([]);
  const [observaciones, setObservaciones] = useState('');
  const [proveedores, setProveedores] = useState([
    {
      nombre: '',
      codigo: '',
      facturas: '',
      orden_compra: '',
      remito: '',
      documentos: [],
      bultos: { heladera: 0, caja: 0, bolsa: 0, pallet: 0, unidad: 0 }
    }
  ]);
  const [mensaje, setMensaje] = useState('');
  const [error, setError] = useState('');

  const [proveedoresLista, setProveedoresLista] = useState([]);
  const [cargandoProv, setCargandoProv] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const fetchHorasParaFecha = useCallback(async (date, horaPreseleccionada) => {
    try {
      const { data } = await api.get(`/turnos/disponibles?fecha=${date.toISOString().split('T')[0]}`);
      let horas = Array.isArray(data.horas) ? data.horas : [];
      horas = asegurarHoraEnLista(horas, horaPreseleccionada);
      setHorasDisponibles(horas);
    } catch (e) {
      console.error('❌ Error al obtener horarios para la fecha:', e);
      setError('Error al obtener horarios para la fecha seleccionada.');
      setHorasDisponibles([]);
    }
  }, []);

  const prefillFromTurno = useCallback(async (t) => {
    if (!t) return;

    const f = t.fecha_turno
      ? (t.fecha_turno instanceof Date ? t.fecha_turno : new Date(`${t.fecha_turno}T00:00:00`))
      : null;

    setFechaTurno(f);
    setObservaciones(t.observaciones || '');

    const provs = Array.isArray(t.proveedor_bultos) && t.proveedor_bultos.length > 0
      ? t.proveedor_bultos.map(p => ({
          nombre: p.nombre || '',
          codigo: p.codigo || p.sap_id || '',
          facturas: Array.isArray(p.facturas) ? p.facturas.join(', ') : (p.facturas || ''),
          orden_compra: p.orden_compra || '',
          remito: p.remito || '',
          documentos: [],
          bultos: p.bultos || { heladera: 0, caja: 0, bolsa: 0, pallet: 0, unidad: 0 }
        }))
      : [{
          nombre: '',
          codigo: '',
          facturas: '',
          orden_compra: '',
          remito: '',
          documentos: [],
          bultos: { heladera: 0, caja: 0, bolsa: 0, pallet: 0, unidad: 0 }
        }];

    setProveedores(provs);

    const hDesde = t.hora_desde || '';
    const hHasta = t.hora_hasta || (hDesde ? sumar30Min(hDesde) : '');
    setHoraDesde(hDesde);
    setHoraHasta(hHasta);

    if (f) await fetchHorasParaFecha(f, hDesde);
  }, [fetchHorasParaFecha]);

  useEffect(() => {
    const fetchDisponibilidad = async () => {
      try {
        const responseFechas = await api.get('/turnos/disponibles');
        setFechasDisponibles((responseFechas.data.fechas || []).map(fecha => new Date(fecha)));
      } catch (error) {
        console.error('❌ Error al obtener disponibilidad:', error);
        setError('Error al obtener disponibilidad de turnos.');
      }
    };

    const fetchProveedores = async () => {
      setCargandoProv(true);
      try {
        const token = localStorage.getItem('token');
        const { data } = await api.get('/turnos/proveedores', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProveedoresLista((data.proveedores || []).map(p => ({
          id: p.id,
          nombre: p.nombre,
          codigo: p.codigo || p.sap_id || ''
        })));
      } catch (err) {
        console.error('❌ Error al obtener proveedores:', err);
        setError('No se pudo cargar la lista de proveedores.');
      } finally {
        setCargandoProv(false);
      }
    };

    fetchDisponibilidad();
    fetchProveedores();
  }, []);

  useEffect(() => {
    const stateTurno = location.state?.prefillTurno;
    const qId = searchParams.get('turnoId');

    if (stateTurno) {
      prefillFromTurno(stateTurno);
      return;
    }

    if (qId) {
      (async () => {
        try {
          const token = localStorage.getItem('token');
          const { data } = await api.get(`/turnos/${qId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          await prefillFromTurno(data);
        } catch (e) {
          console.error('No se pudo prellenar el turno:', e);
        }
      })();
    }
  }, [location.state, searchParams, prefillFromTurno]);

  const handleFechaChange = async (date) => {
    setFechaTurno(date);
    setHorasDisponibles([]);
    setHoraDesde('');
    setHoraHasta('');
    if (date) await fetchHorasParaFecha(date, null);
  };

  const handleHoraDesdeChange = (horaSeleccionada) => {
    setHoraDesde(horaSeleccionada);
    setHoraHasta(sumar30Min(horaSeleccionada));
  };

  const handleProveedorChange = (index, field, value) => {
    const updated = [...proveedores];
    updated[index][field] = value;
    if (field === 'nombre') {
      updated[index].codigo = '';
    }
    setProveedores(updated);
  };

  const handleProveedorPick = (index, sug) => {
    const updated = [...proveedores];
    updated[index].nombre = sug?.nombre || '';
    updated[index].codigo = sug?.codigo || '';
    setProveedores(updated);
  };

  const handleBultoChange = (index, tipo, value) => {
    const updated = [...proveedores];
    updated[index].bultos[tipo] = Math.max(0, parseInt(value) || 0);
    setProveedores(updated);
  };

  const handleFileChange = (index, files) => {
    const updated = [...proveedores];
    updated[index].documentos = Array.from(files);
    setProveedores(updated);
  };

  const agregarProveedor = () => {
    setProveedores(prev => [
      ...prev,
      {
        nombre: '',
        codigo: '',
        facturas: '',
        orden_compra: '',
        remito: '',
        documentos: [],
        bultos: { heladera: 0, caja: 0, bolsa: 0, pallet: 0, unidad: 0 }
      }
    ]);
  };

  const eliminarProveedor = (index) => {
    if (proveedores.length > 1) {
      setProveedores(proveedores.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMensaje('');

    const confirmar = window.confirm('¿Estás seguro de que querés solicitar el turno?');
    if (!confirmar) return;

    setIsSubmitting(true);

    if (!fechaTurno || !horaDesde || !horaHasta) {
      setError('Debe seleccionar una fecha y un rango horario.');
      setIsSubmitting(false);
      return;
    }

    if (horaDesde >= horaHasta) {
      setError('La hora de inicio debe ser menor que la de fin.');
      setIsSubmitting(false);
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setError('No estás autenticado.');
      setIsSubmitting(false);
      return;
    }

    const archivosInvalidos = proveedores.flatMap(prov =>
      (prov.documentos || []).filter(file => file.type !== 'application/pdf')
    );
    if (archivosInvalidos.length > 0) {
      setError('Uno o más archivos seleccionados no son PDF.');
      setIsSubmitting(false);
      return;
    }

    const proveedoresJSON = JSON.stringify(proveedores.map(prov => ({
      nombre: prov.nombre || '',
      codigo: prov.codigo || '',
      facturas: prov.facturas ? prov.facturas.split(',').map(f => f.trim()).filter(Boolean) : [],
      orden_compra: prov.orden_compra || '',
      remito: prov.remito || '',
      bultos: prov.bultos || {}
    })));

    const formData = new FormData();
    formData.append('fecha_turno', fechaTurno.toISOString().split('T')[0]);
    formData.append('hora_desde', horaDesde);
    formData.append('hora_hasta', horaHasta);
    formData.append('observaciones', observaciones || '');
    formData.append('proveedor_bultos', proveedoresJSON);

    proveedores.forEach((prov) => {
      if (prov.documentos && prov.documentos.length > 0) {
        prov.documentos.forEach((file) => {
          formData.append('documentos', file);
        });
      }
    });

    try {
      await api.post('/turnos', formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setMensaje('Turno solicitado con éxito. Pendiente de aprobación.');
      setTimeout(() => navigate('/dashboard'), /* 2000 */);
    } catch (error) {
      console.error('❌ Error al solicitar turno:', error);
      setError(error.response?.data?.error || 'Error al solicitar el turno.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // UI 
  return (
    <div className="container mt-5">
      <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>⬅ Volver al Dashboard</button>
      <br /><br />
      <h2>Solicitar Turno</h2>
      {error && <div className="alert alert-danger">{error}</div>}
      {mensaje && <div className="alert alert-success">{mensaje}</div>}

      <form onSubmit={handleSubmit}>
        <label>Fecha del Turno</label><br />
        <DatePicker
          selected={fechaTurno}
          onChange={handleFechaChange}
          includeDates={fechasDisponibles}
          dateFormat="yyyy-MM-dd"
          className="form-control"
          required
        />

        <br />
        <label>Hora Desde</label>
        <select className="form-control" value={horaDesde} onChange={(e) => handleHoraDesdeChange(e.target.value)} required>
          <option value="">Selecciona una hora</option>
          {horasDisponibles.map((hora, index) => (
            <option key={index} value={hora}>{hora}</option>
          ))}
        </select>

        <label>Observaciones (opcional)</label>
        <textarea
          className="form-control"
          value={observaciones}
          onChange={(e) => setObservaciones(e.target.value)}
          rows="3"
          maxLength="140"
          placeholder="Ingrese observaciones..."
        />
        <hr />

        {proveedores.map((proveedor, index) => (
          <div key={index} className="border p-3 mb-3">
            <h5>Proveedor {index + 1}</h5>

            <label>Nombre del Proveedor</label>
            <AutocompleteProveedor
              value={proveedor.nombre}
              onChange={(nombre) => handleProveedorChange(index, 'nombre', nombre)}
              onPick={(sug) => handleProveedorPick(index, sug)}
              options={proveedoresLista}
              loading={cargandoProv}
              placeholder="Escribí o seleccioná un proveedor"
            />
            {proveedor.codigo ? (
              <small className="text-muted">Código seleccionado: {proveedor.codigo}</small>
            ) : null}

            <br />
            <input
              type="text"
              className="form-control"
              placeholder="Facturas (separadas por coma)"
              value={proveedor.facturas}
              onChange={(e) => handleProveedorChange(index, 'facturas', e.target.value)}
            />
            <br />
            <input
              type="text"
              className="form-control"
              placeholder="Orden de Compra (opcional)"
              value={proveedor.orden_compra}
              onChange={(e) => handleProveedorChange(index, 'orden_compra', e.target.value)}
            />
            <br />
            <input
              type="text"
              className="form-control"
              placeholder="Número de Remito"
              value={proveedor.remito}
              onChange={(e) => handleProveedorChange(index, 'remito', e.target.value)}
            />
            <br />
            <input
              type="file"
              className="form-control"
              accept="application/pdf"
              multiple
              onChange={(e) => handleFileChange(index, e.target.files)}
            />
            <br />
            <label>Tipos de Bultos</label>
            <div className="row">
              {Object.entries(proveedor.bultos).map(([tipo, cantidad]) => (
                <div key={`${index}-${tipo}`} className="col-md-2 d-flex flex-column align-items-center">
                  <label>{tipo.charAt(0).toUpperCase() + tipo.slice(1)}</label>
                  <input
                    type="number"
                    className="form-control text-center"
                    min="0"
                    value={cantidad}
                    onChange={(e) => handleBultoChange(index, tipo, e.target.value)}
                  />
                </div>
              ))}
            </div>

            <button type="button" className="btn btn-danger mt-2" onClick={() => eliminarProveedor(index)}>
              Eliminar Proveedor
            </button>
          </div>
        ))}

        <button type="button" className="btn btn-secondary mt-3" onClick={agregarProveedor}>Agregar otro proveedor</button>
        <br />
        <button type="submit" className="btn btn-primary mt-3" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Cargando...
            </>
          ) : (
            'Solicitar Turno'
          )}
        </button>
      </form>
    </div>
  );
};

export default SolicitarTurno;
